import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const leCannetEn: ChauffeurPage = {
  slug: "le-cannet",
  title: "VTC & private chauffeur in Le Cannet",
  subtitle:
    "Private chauffeur in Le Cannet: Nice airport transfers, the Bonnard museum, the old village and quick links to Cannes, 7 days a week.",
  keywords: [
    "vtc le cannet",
    "private chauffeur le cannet",
    "le cannet airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Le Cannet",
    subtitle:
      "Nice airport transfers, links to Cannes, the Bonnard museum and the old village — available 24/7.",
    keywords: [
      "VTC Le Cannet",
      "Rocheville chauffeur",
      "Nice airport transfer",
      "Cannes link",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Le Cannet",
    badges: ["Available 24/7", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Le Cannet",
    items: [
      {
        title: "Nice airport, door to door",
        description:
          "Nice airport around 35 minutes away via the A8, with flight tracking and a personal welcome.",
      },
      {
        title: "At the gates of Cannes",
        description:
          "On the residential heights above Cannes: the Croisette and the Palais des Festivals just minutes away.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "French/English, punctual and discreet, at ease in the residential districts and the hills.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Le Cannet",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up with flight tracking to and from Nice Côte d’Azur airport and the Cannes stations.",
      },
      {
        title: "Links to Cannes",
        text: "Shuttles to the Croisette, the Palais des Festivals, the hotels and the port, with no parking worries.",
      },
      {
        title: "Chauffeur at your disposal",
        text: "By the hour or the day, a dedicated chauffeur for your meetings, visits and outings.",
      },
      {
        title: "Culture & discovery",
        text: "Bonnard museum, old village and Place Bellevue, with drop-off as close as possible to the pedestrian lanes.",
      },
      {
        title: "Long distance",
        text: "Any-distance trips from Le Cannet: Monaco, Saint-Tropez, Italy and ski resorts.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Bonnard museum",
        description:
          "The only museum devoted to the painter Pierre Bonnard, in the heart of Le Cannet.",
      },
      {
        name: "Old village (Vieux Cannet)",
        description: "Historic village, pedestrian lanes and painted façades.",
      },
      {
        name: "Place Bellevue",
        description: "Belvedere with a panoramic view over the bay of Cannes.",
      },
      {
        name: "Saint-Sauveur chapel",
        description: "Chapel decorated by the artist Théo Tobiasse.",
      },
      {
        name: "Rocheville",
        description: "Residential and shopping district of Le Cannet.",
      },
      {
        name: "Nice airport",
        description: "About 35 minutes away via the A8 motorway.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur across Le Cannet",
    subheading:
      "From the residential heights to the Croisette, premium service with no parking.",
    paragraphs: [
      "KS Rent Services handles your journeys with a private chauffeur in Le Cannet and across the French Riviera: transfers from Nice airport (about 35 minutes), links with Cannes and long-distance trips.",
      "Le Cannet rises across the hills just above Cannes: our bilingual chauffeurs know the residential districts, Rocheville and the old village, and connect you to the Croisette in minutes.",
      "For a visit to the Bonnard museum, the panorama at Place Bellevue or an evening in Cannes, your chauffeur drops you as close as possible and collects you on request, with no circling for a space.",
    ],
    bullets: [
      "Flight tracking & personal welcome",
      "Fast links with Cannes",
      "Drop-off near the lanes",
    ],
  },
  process: {
    heading: "Book a chauffeur in Le Cannet",
    steps: [
      "Share your route, timings and number of passengers.",
      "We confirm the right vehicle and the fixed fare.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Live adjustments for traffic and any last-minute changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Le Cannet"),
    {
      q: "How long is the transfer to Nice airport from Le Cannet?",
      a: "Around 35 minutes via the A8 motorway in normal conditions. We run transfers 24/7 with flight tracking and a personal welcome.",
    },
    {
      q: "Do you link with Cannes and the Croisette?",
      a: "Yes, Le Cannet adjoins Cannes: we reach the Croisette, the Palais des Festivals and the hotels within minutes, with no parking worries.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to international guests.",
    },
    {
      q: "Which vehicles do you offer in Le Cannet?",
      a: "Mercedes E-Class and S-Class sedans, 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the major Cannes events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do you take visitors to the Bonnard museum and the old village?",
      a: "Yes, with drop-off as close as possible to the pedestrian lanes and pick-up on request, for the Bonnard museum, Place Bellevue and the old village.",
    },
  ],
  relatedLinks: [
    { label: "Airport transfers in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Cannes car rental rates",
      href: "/en/location/car-rental-cannes",
    },
  ],
  cta: {
    title: "Your private chauffeur in Le Cannet",
    text: "Book your transfer or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Le Cannet — private chauffeur & transfers",
    description:
      "Private car service in Le Cannet: Nice airport transfer (~35 min), links to Cannes and the Croisette, Bonnard museum, old village. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/le-cannet",
  },
};

export default leCannetEn;
